/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Button, Card, Icon, Modal, Tooltip } from '@scuf/common';
import { InjectLocalization, useTranslation } from '@scuf/localization';
import { Notification } from '@scuf/common';
import React, { memo, useContext, useState } from 'react';
import { ApiContext } from '../../../../context/ApiContext';
import descriptors from '../../../../locales/en-US/descriptors';
import DetailSummaryComponent, {
  IDetailAggregationData
} from '../../../shared/DetailSummaryComponent/DetailSummaryComponent';
import {
  getActiveDurationChartData,
  getActualConsumptionChartData,
  getEmsopStrategyStatus,
  getEnergySavingsChartData,
  getEoLastStatus,
  getEOLastTooltip,
  getExcludedDaysChartData,
  getInactiveDaysChartData,
  getStrategyLastUpdated,
  getUnit,
  getEOStatusRunTime,
  getStrategyLastUpdatedForEOStatus
} from '../../../shared/Utils/EnergyOptimizationUtils';
import {
  IEnergyOptimizationChartDataProps,
  IEnergyOptimizationWidgetProps
} from '../EnergyOptimizationInterfaces';
import EnergyOptimizationChart from './EnergyOptimizationChart';
import EnergyOptimizationSavingsSummary from './EnergyOptimizationSavingsSummary';
import EnergyOptimizationStrategyStatus from './EnergyOptimizationStrategyStatus';
import { eoScalingAndConversionForUtilityConsumption } from './EODetailsByUtility/EODetailsByUtilityUtils';
import { IAny } from '../../IEnergyManagementProps';
import {
  getConvertedValue,
  getMaxConvertedValueFromArray,
  getMaxConvertedValueFromArrayForUtilitySummary,
  UnitSymbols
} from '../../../shared/Utils/unitHandling/unitHandling';

const d: IAny = descriptors.energyManagement;

const EnergyOptimizationWidget = ({
  data,
  mainGraph = true,
  isAllUtility = false
}: IEnergyOptimizationWidgetProps) => {
  const [t, lng] = useTranslation();

  const apiContext = useContext(ApiContext);
  const {
    onEnergyOptimizationViewDetailsClick,
    currencyCode,
    userPreferedUnit,
    lastUpdated,
    toggleEnableEoStatus
  } = apiContext;

  const onViewDetailsClick = () => {
    if (onEnergyOptimizationViewDetailsClick) {
      onEnergyOptimizationViewDetailsClick();
    }
  };

  const chartData = {
    inactiveDays: getInactiveDaysChartData(data.dataItems),
    excludedDays: getExcludedDaysChartData(data.dataItems),
    actualConsumption: getActualConsumptionChartData(data.dataItems),
    energySavings: getEnergySavingsChartData(data.dataItems),
    activeHours: getActiveDurationChartData(data.dataItems),
    runTimeEOStatus: apiContext.toggleEnableEoStatus
      ? getEOStatusRunTime(data.dataItems)
      : []
  } as IEnergyOptimizationChartDataProps;

  const emsopStrategyStatus = getEmsopStrategyStatus(data);
  const dataMeasurementUnit = getUnit(data.dataItems);
  let measurementUnit = dataMeasurementUnit;
  let costSavingsUnit = measurementUnit;
  let graphMeasurementUnit = dataMeasurementUnit;
  const strategyVal = toggleEnableEoStatus
    ? getStrategyLastUpdatedForEOStatus(lastUpdated)
    : getStrategyLastUpdated(data);
  const strategyLastUpdated =
    strategyVal != undefined
      ? strategyVal.locale(lng).format('DD MMM YYYY h:mm a')
      : undefined;

  const {
    summary: {
      actualSavings,
      calculatedSavings,
      estimatedSavings,
      totalEODeviationPercentage,
      operationalTimePercentage,
      eoEnableDaysSavingPercentage,
      entirePeriodSavingPercentage,
      timeUsedForBaseline,
      opportunityFromEO
    },
    aggregationsStartDate,
    aggregationsEndDate,
    utilityType
  } = data;

  let aggregationSummaryData: IDetailAggregationData = {
    actualCost: actualSavings,
    baselineCost: calculatedSavings,
    costSavings: estimatedSavings,
    eoCostSavingsUnit: costSavingsUnit,
    costSavingsPercentage: isNaN(totalEODeviationPercentage)
      ? 0
      : totalEODeviationPercentage,
    eoEnableDaysSavingPercentage: isNaN(eoEnableDaysSavingPercentage)
      ? 0
      : eoEnableDaysSavingPercentage,
    entirePeriodSavingPercentage: isNaN(entirePeriodSavingPercentage)
      ? 0
      : entirePeriodSavingPercentage
  };
  let baselineCostUnit;

  // For EO By Utility Charts
  if (!!utilityType && utilityType !== 'all') {
    let {
      actualCost,
      baselineCost,
      costSavings,
      measurementUnit: convertedMeasurementUnit,
      chartDataUnit,
      costSavingsUnit: convertedCostSavingsUnit,
      baselineCostUnit: convertedBaselineCostUnit,
      actualConsumption,
      energySavings,
      inactiveDays,
      excludedDays,
      runTimeEOStatus
    } = eoScalingAndConversionForUtilityConsumption(
      aggregationSummaryData,
      chartData,
      utilityType,
      dataMeasurementUnit,
      userPreferedUnit
    );
    measurementUnit = convertedMeasurementUnit;
    baselineCostUnit = convertedBaselineCostUnit;
    graphMeasurementUnit = chartDataUnit;
    costSavingsUnit = convertedCostSavingsUnit;
    aggregationSummaryData = {
      ...aggregationSummaryData,
      actualCost,
      baselineCost,
      costSavings,
      eoCostSavingsUnit: costSavingsUnit
    };
    chartData.actualConsumption = actualConsumption;
    chartData.energySavings = energySavings;
    chartData.inactiveDays = inactiveDays;
    chartData.excludedDays = excludedDays;
    chartData.runTimeEOStatus = runTimeEOStatus;
  }

  const typeMapper: IAny = {
    electricity: 'Electricity',
    gas: 'Gas',
    steam: 'Steam',
    water: 'Water'
  };

  let covertedOpportunity;
  let unitForOpportunity;

  if (data.utilityType?.toLowerCase() !== 'all') {
    unitForOpportunity = getMaxConvertedValueFromArray(
      data.utilityType ? typeMapper[data.utilityType] : 'Electricity',
      dataMeasurementUnit,
      [data.summary.opportunityFromEO ? data.summary.opportunityFromEO : 0]
    ).Unit;
    covertedOpportunity = getConvertedValue(
      data.utilityType ? typeMapper[data.utilityType] : 'Electricity',
      dataMeasurementUnit ? dataMeasurementUnit : 'kilowatt hour',
      data.summary.opportunityFromEO ? data.summary.opportunityFromEO : 0,
      unitForOpportunity
    ).Value;
  }

  const getReasonForOff = (reasons: string[] | string) => {
    return (
      <ul>
        {reasons &&
          reasons !== 'NA' &&
          reasons.length &&
          (reasons as string[]).map((reason: string) => {
            return <li key={reason}>{reason}</li>;
          })}
      </ul>
    );
  };

  return (
    <>
      {mainGraph && (
        <Card.Header
          className={`kpi-header-container ${
            !mainGraph && !!utilityType ? 'eo-with-utility-tabs' : ''
          }`}
        >
          <div className="energy-optimization-header-title">
            <EnergyOptimizationStrategyStatus
              strategyStatus={
                apiContext.toggleEnableEoStatus
                  ? getEoLastStatus(
                      apiContext.eoStatus ? apiContext.eoStatus : 0
                    )
                  : emsopStrategyStatus
              }
              strategyLastUpdated={strategyLastUpdated}
              mainGraph={mainGraph}
              toggleEnableEoStatus={apiContext.toggleEnableEoStatus}
            />
            <div>
              <Tooltip
                element={<Icon root="common" name="badge-info" size="small" />}
                event="hover"
                content={
                  apiContext.toggleEnableEoStatus
                    ? apiContext.eoStatus === 2
                      ? apiContext.reasonForOff &&
                        apiContext.reasonForOff.length &&
                        apiContext.reasonForOff.length > 0 &&
                        getReasonForOff(apiContext.reasonForOff)
                      : (t(
                          d[
                            getEOLastTooltip(
                              apiContext.eoStatus ? apiContext.eoStatus : 0
                            )
                          ]
                        ) as IAny)
                    : t(d.EMAASEOSTATETOOLTIP)
                }
                position="right center"
                size="small"
              />
            </div>
          </div>
        </Card.Header>
      )}
      <Card.Content
        className={`energy-optimization-widget-card-content kpi-content-container ${!mainGraph &&
          'eo-notMainGraph'}`}
      >
        {!mainGraph && (
          <div className="savings-div-header">
            <div className="eoSavingsHeading">{t(d.EOSAVINGS)} </div>
            {apiContext.toggleEnableEoStatus && (
              <div className="energy-optimization-View-header-title">
                <EnergyOptimizationStrategyStatus
                  strategyStatus={getEoLastStatus(
                    apiContext.eoStatus ? apiContext.eoStatus : 0
                  )}
                  strategyLastUpdated={strategyLastUpdated}
                  mainGraph={false}
                  toggleEnableEoStatus={apiContext.toggleEnableEoStatus}
                />
                <Tooltip
                  element={
                    <Icon
                      className="currentStatusTooltip"
                      root="common"
                      name="badge-info"
                      size="small"
                    />
                  }
                  event="hover"
                  content={
                    apiContext.toggleEnableEoStatus
                      ? apiContext.eoStatus === 2
                        ? apiContext.reasonForOff &&
                          apiContext.reasonForOff.length &&
                          apiContext.reasonForOff.length > 0 &&
                          getReasonForOff(apiContext.reasonForOff)
                        : (t(
                            d[
                              getEOLastTooltip(
                                apiContext.eoStatus ? apiContext.eoStatus : 0
                              )
                            ]
                          ) as IAny)
                      : t(d.EMAASEOSTATETOOLTIP)
                  }
                  position="right center"
                  size="small"
                />
              </div>
            )}
          </div>
        )}
        {mainGraph ? (
          <EnergyOptimizationSavingsSummary
            currencyName={currencyCode}
            estimatedSavings={{
              monetaryValue: estimatedSavings,
              decimalPercentage: totalEODeviationPercentage
            }}
            operationalTimePercentage={operationalTimePercentage}
          />
        ) : (
          <DetailSummaryComponent
            aggregationData={aggregationSummaryData}
            costCurrency={currencyCode}
            detailType={`energyOptimizationSummary${
              !!utilityType && utilityType !== 'all' ? 'UtilityConsumption' : ''
            }`}
            measurementUnit={measurementUnit}
            baselineCostUnit={baselineCostUnit}
            operationalTimePercentage={operationalTimePercentage}
            timeUsedForBaseline={timeUsedForBaseline}
            opportunityFromEO={opportunityFromEO}
            opportunityUnit={unitForOpportunity}
            opportunityConverted={covertedOpportunity ? covertedOpportunity : 0}
          />
        )}
        <div className="energy-optimization-visualization-content">
          <div className="energy-optimization-savings-content kpi-card-flex-container">
            {(totalEODeviationPercentage ? totalEODeviationPercentage : 0) <=
              0 && (
              <div className="savings-notification-container">
                <Notification
                  className="savings-notification"
                  hasIcon={true}
                  severity="information"
                >
                  {t(d.EONEGATIVEORZEROSAVINGSMESSAGE)}
                </Notification>
              </div>
            )}
            <EnergyOptimizationChart
              chartData={chartData}
              showComfortData={false}
              currencyName={currencyCode}
              startTime={aggregationsStartDate}
              height={200}
              endTime={aggregationsEndDate}
              showInactiveDaysData={Boolean(chartData.inactiveDays?.length)}
              showExcludedDaysData={Boolean(chartData.excludedDays?.length)}
              mainGraph={mainGraph}
              isConsumptionGraph={!!utilityType && utilityType !== 'all'}
              measurementUnit={graphMeasurementUnit}
              isAllUtility={isAllUtility}
            />
            {mainGraph && (
              <div className="kpi-card-view-details">
                <Button
                  type="link"
                  content={t(d.VIEWDETAILS)}
                  className="view-details-button"
                  onClick={() => onViewDetailsClick()}
                />
              </div>
            )}
          </div>
        </div>
      </Card.Content>
    </>
  );
};

export default memo(EnergyOptimizationWidget);
